import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { currency } from "@/lib/format";
import BillSummary from "@/components/payment/BillSummary";
import TipSelector from "@/components/payment/TipSelector";
import PaymentMethodPicker from "@/components/payment/PaymentMethodPicker";
import AddCardDialog from "@/components/payment/AddCardDialog";
import PaymentReceipt from "@/components/payment/PaymentReceipt";

export default function PaymentPage() {
    const { reservationId } = useParams();
    const navigate = useNavigate();

    const [bill, setBill] = useState(null);
    const [cards, setCards] = useState([]);
    const [selectedCardId, setSelectedCardId] = useState("");
    const [tipPct, setTipPct] = useState(18);
    const [customTip, setCustomTip] = useState("");
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [receipt, setReceipt] = useState(null);
    const [addOpen, setAddOpen] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const [billRes, cardsRes] = await Promise.all([
                api.get(`/reservations/${reservationId}/bill`),
                api.get("/payments/cards"),
            ]);
            setBill(billRes.data);
            setCards(cardsRes.data || []);
            const def = cardsRes.data?.find((x) => x.is_default) || cardsRes.data?.[0];
            if (def) setSelectedCardId(def.id);
        } catch (error) {
            console.warn("[payment] load failed", error?.message);
            toast.error("Could not load payment details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservationId]);

    const tipAmount = customTip
        ? parseFloat(customTip) || 0
        : bill
            ? (bill.subtotal * tipPct) / 100
            : 0;
    const total = bill ? bill.total_before_tip + tipAmount : 0;

    const pay = async () => {
        if (!selectedCardId) {
            toast.error("Select a card");
            return;
        }
        setProcessing(true);
        setProgress(10);
        const tick = setInterval(() => setProgress((p) => Math.min(p + 15, 90)), 380);
        try {
            const { data } = await api.post("/payments/charge", {
                reservation_id: reservationId,
                card_id: selectedCardId,
                tip: tipAmount,
            });
            clearInterval(tick);
            setProgress(100);
            await new Promise((r) => setTimeout(r, 600));
            setReceipt(data.receipt);
            toast.success("Payment successful!");
        } catch (error) {
            clearInterval(tick);
            console.warn("[payment] charge failed", error?.message);
            setProcessing(false);
            setProgress(0);
            toast.error(error?.response?.data?.detail || "Payment failed");
        }
    };

    if (loading) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-8">
                <Skeleton className="h-96" />
            </div>
        );
    }
    if (!bill) return null;

    if (receipt) {
        return <PaymentReceipt receipt={receipt} onDone={() => navigate("/reservations")} />;
    }

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 grid lg:grid-cols-[1fr_360px] gap-6">
            <div>
                <h1 className="font-display text-3xl mb-1">Complete payment</h1>
                <p className="text-sm text-muted-foreground mb-6">
                    Review your bill, add a tip, and confirm.
                </p>

                <BillSummary bill={bill} tipAmount={tipAmount} total={total} />

                <TipSelector
                    subtotal={bill.subtotal}
                    tipPct={tipPct}
                    customTip={customTip}
                    onChangePct={(p) => {
                        setCustomTip("");
                        setTipPct(p);
                    }}
                    onChangeCustomTip={setCustomTip}
                />
            </div>

            <div>
                <Card>
                    <CardContent className="p-6">
                        <h2 className="font-display text-xl mb-3">Payment method</h2>
                        <PaymentMethodPicker
                            cards={cards}
                            selectedCardId={selectedCardId}
                            onSelect={setSelectedCardId}
                            onAddCardClick={() => setAddOpen(true)}
                        />
                        <Button
                            className="w-full mt-4 h-11"
                            onClick={pay}
                            disabled={processing || !cards.length}
                            data-testid="pay-now-button"
                        >
                            {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            <Receipt className="h-4 w-4 mr-2" /> Pay {currency(total)}
                        </Button>
                    </CardContent>
                </Card>
            </div>

            <Dialog open={processing} onOpenChange={() => {}}>
                <DialogContent className="sm:max-w-md" data-testid="payment-processing-modal">
                    <DialogHeader>
                        <DialogTitle>Processing payment</DialogTitle>
                    </DialogHeader>
                    <ProcessingState progress={progress} />
                </DialogContent>
            </Dialog>

            <AddCardDialog open={addOpen} onOpenChange={setAddOpen} onAdded={load} />
        </div>
    );
}

function ProcessingState({ progress }) {
    let label = "Authorizing…";
    if (progress >= 30 && progress < 70) label = "Verifying card…";
    else if (progress >= 70 && progress < 100) label = "Capturing payment…";
    else if (progress >= 100) label = "Complete";
    return (
        <div className="space-y-3">
            <Progress value={progress} />
            <p className="text-sm text-muted-foreground" data-testid="payment-processing-status">
                {label}
            </p>
        </div>
    );
}
